/**
 * intelligence.js
 *
 * Pure helpers for the cost-intelligence and stock-intelligence screens and the
 * dashboard cards. No React, no network — unit-tested in
 * `app/tests/intelligence.test.js`.
 *
 * @module helpers/intelligence
 */
import {classifyDelta, formatKpiValue, formatNumber, toDateKey} from './businessAi';

/** Backend `reason` codes that have a dedicated empty-state message. */
export const INTEL_REASONS = ['NO_RECIPES', 'NO_STOCK', 'NO_HISTORY', 'NO_RECEIPTS', 'NO_SALES', 'NO_WASTE', 'NO_ACTUAL_DATA', 'NO_CHANGES', 'NO_MOVEMENT_DATA'];

/**
 * Locale key of the empty-state copy for a backend `reason`.
 * @param {*} reason reason code (`null` → generic).
 * @returns {string} `intel.reason_<CODE>` or `intel.reason_generic`.
 */
export const reasonMessageKey = (reason) => {
    const code = String(reason || '').toUpperCase();

    return INTEL_REASONS.includes(code) ? `intel.reason_${code}` : 'intel.reason_generic';
};

const RISK_META = {
    stockout: {key: 'stockout', tone: 'danger', icon: 'alert-octagon', color: '#EF4444', rank: 0},
    low: {key: 'low', tone: 'warning', icon: 'alert', color: '#F97316', rank: 1},
    overstock: {key: 'overstock', tone: 'info', icon: 'package-variant', color: '#3B82F6', rank: 2},
    ok: {key: 'ok', tone: 'success', icon: 'check-circle-outline', color: '#10B981', rank: 3},
};

/**
 * Stock risk → display metadata (unknown → ok, lowest priority).
 * @param {string} risk `ok` | `low` | `stockout` | `overstock`.
 * @returns {{key: string, tone: string, icon: string, color: string, rank: number}} meta.
 */
export const getRiskMeta = (risk) => RISK_META[risk] || RISK_META.ok;

/**
 * Risk-sorted copy of forecast items: stockout first, then fewest days of cover.
 * @param {Array<object>} items forecast items.
 * @returns {Array<object>} sorted copy.
 */
export const sortByRisk = (items) => (Array.isArray(items) ? items : []).filter(Boolean).slice().sort((a, b) => {
    const rank = getRiskMeta(a.risk).rank - getRiskMeta(b.risk).rank;

    if (rank) return rank;
    const cover = (v) => (Number.isFinite(Number(v)) && v !== null ? Number(v) : Infinity);

    return cover(a.daysOfCover) - cover(b.daysOfCover);
});

/**
 * Items at stockout risk, most urgent first.
 * @param {Array<object>} items forecast items.
 * @param {number} [limit=2] max items.
 * @returns {Array<object>} risky items.
 */
export const pickRiskItems = (items, limit = 2) => sortByRisk(items).filter((item) => item.risk === 'stockout' || item.risk === 'low').slice(0, limit);

/**
 * The weakest product by margin (lowest numeric `marginPct`; products without cost data are ignored).
 * @param {Array<object>} items margin items.
 * @returns {?object} the item, or `null`.
 */
export const pickWorstMargin = (items) => (Array.isArray(items) ? items : [])
    .filter((item) => item && item.marginPct !== null && item.marginPct !== undefined && Number.isFinite(Number(item.marginPct)))
    .reduce((worst, item) => (!worst || Number(item.marginPct) < Number(worst.marginPct) ? item : worst), null);

/**
 * Margin vs target status.
 * @param {*} marginPct margin in percent.
 * @param {*} targetPct target margin in percent.
 * @returns {'below'|'ok'|'unknown'} status.
 */
export const marginStatus = (marginPct, targetPct) => {
    if (marginPct === null || marginPct === undefined || !Number.isFinite(Number(marginPct))) return 'unknown';
    if (targetPct === null || targetPct === undefined || !Number.isFinite(Number(targetPct))) return 'ok';

    return Number(marginPct) < Number(targetPct) ? 'below' : 'ok';
};

/**
 * Margin bar fill ratio 0..1 (margin relative to twice the target so the target marker sits mid-bar).
 * @param {*} marginPct margin in percent.
 * @param {*} targetPct target in percent.
 * @returns {{fill: number, target: number}} ratios.
 */
export const marginBarRatios = (marginPct, targetPct) => {
    const target = Number(targetPct) > 0 ? Number(targetPct) : 50;
    const scale = Math.max(target * 2, Number(marginPct) || 0, 1);

    return {
        fill: Math.max(0, Math.min(1, (Number(marginPct) || 0) / scale)),
        target: Math.max(0, Math.min(1, target / scale)),
    };
};

/**
 * Classify a margin change in percentage points (higher margin is good).
 * @param {*} pts delta in points.
 * @returns {{direction: string, tone: string}} classification.
 */
export const classifyMarginDelta = (pts) => classifyDelta(pts);

/**
 * Classify an ingredient price change (a price rise is bad).
 * @param {*} changePct price change in percent.
 * @returns {{direction: string, tone: string}} classification.
 */
export const classifyPriceChange = (changePct) => classifyDelta(changePct, {invert: true});

/**
 * Signed points text, e.g. `+2,5 pt`.
 * @param {*} pts delta in percentage points.
 * @returns {?string} text or `null`.
 */
export const formatPts = (pts) => {
    if (pts === null || pts === undefined || pts === '' || !Number.isFinite(Number(pts))) return null;
    const rounded = Math.round(Number(pts) * 10) / 10;

    return `${rounded > 0 ? '+' : ''}${formatNumber(rounded, 1)} pt`;
};

/**
 * Money text with currency, e.g. `12 500 HUF`.
 * @param {*} value amount.
 * @param {?string} currency currency code.
 * @returns {string} text.
 */
export const formatMoney = (value, currency) => formatKpiValue(value, currency || '');

/**
 * Quantity text with unit, 2 decimals max.
 * @param {*} qty quantity.
 * @param {?string} unit unit.
 * @returns {string} text.
 */
export const formatQty = (qty, unit) => {
    const text = formatNumber(qty, 2);

    return unit && text !== '—' ? `${text} ${unit}` : text;
};

/** ABC class → colour. */
export const ABC_COLORS = {A: '#10B981', B: '#F97316', C: '#94A3B8'};

/**
 * Variance flag → display metadata. Neutral wording is the screen's job; this only picks tone/icon.
 * @param {string} flag `ok` | `watch` | `loss` | `surplus`.
 * @returns {{key: string, tone: string, icon: string, rank: number}} meta (unknown → ok).
 */
export const getVarianceMeta = (flag) => {
    switch (flag) {
        case 'loss':
            return {key: 'loss', tone: 'danger', icon: 'arrow-down-bold-circle-outline', rank: 0};
        case 'watch':
            return {key: 'watch', tone: 'warning', icon: 'eye-outline', rank: 1};
        case 'surplus':
            return {key: 'surplus', tone: 'info', icon: 'arrow-up-bold-circle-outline', rank: 2};
        default:
            return {key: 'ok', tone: 'success', icon: 'check-circle-outline', rank: 3};
    }
};

/**
 * The next `count` days starting tomorrow (the earliest date the prep endpoint accepts).
 * @param {number} [count=7] number of days.
 * @param {Date} [from] reference "today".
 * @returns {Array<{key: string, weekday: number, day: number}>} day chips.
 */
export const nextPrepDays = (count = 7, from = new Date()) => Array.from({length: count}, (_, index) => {
    const date = new Date(from.getFullYear(), from.getMonth(), from.getDate() + index + 1);

    return {key: toDateKey(date), weekday: date.getDay(), day: date.getDate()};
});

/**
 * SVG polyline `points` of a price series, scaled into a box.
 * @param {Array<{price: number}>} series chronological points.
 * @param {number} width box width.
 * @param {number} height box height.
 * @param {number} [pad=2] inner padding.
 * @returns {string} `x,y x,y …` (empty when fewer than 2 numeric points).
 */
export const sparklinePoints = (series, width, height, pad = 2) => {
    const values = (Array.isArray(series) ? series : []).map((point) => Number(point?.price)).filter(Number.isFinite);

    if (values.length < 2) return '';
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;

    return values.map((value, index) => {
        const x = pad + (index / (values.length - 1)) * (width - pad * 2);
        const y = height - pad - ((value - min) / span) * (height - pad * 2);

        return `${Math.round(x * 10) / 10},${Math.round(y * 10) / 10}`;
    }).join(' ');
};

/**
 * Plain-text order list grouped by partner, for sharing. Only items that need
 * ordering (`suggestedOrderQty > 0`) are included; `unitMismatch` items are skipped.
 * @param {Array<object>} items forecast items.
 * @param {{title?: string, noPartner?: string}} [labels] localized labels.
 * @returns {string} text (empty when nothing to order).
 */
export const buildOrderListText = (items, {title = '', noPartner = '-'} = {}) => {
    const groups = new Map();

    (Array.isArray(items) ? items : []).forEach((item) => {
        const qty = Number(item?.suggestedOrderQty);

        if (!item || item.unitMismatch || !Number.isFinite(qty) || qty <= 0) return;
        const partner = item.partnerName || noPartner;

        if (!groups.has(partner)) groups.set(partner, []);
        groups.get(partner).push(`- ${item.name}: ${formatQty(Math.ceil(qty * 100) / 100, item.unit)}`);
    });
    if (!groups.size) return '';
    const blocks = Array.from(groups.keys())
        .sort((a, b) => (a === noPartner) - (b === noPartner) || String(a).localeCompare(String(b)))
        .map((partner) => `${partner}\n${groups.get(partner).join('\n')}`);

    return `${title ? `${title}\n\n` : ''}${blocks.join('\n\n')}`;
};
